// @ts-nocheck
/**
 * 差し押さえ画面
 * 借金返済期限切れ時のカード差し押さえ処理UI
 *
 * 処理フロー:
 * 1. まず所持金をすべて返済に使用
 * 2. 所持金で完済できた場合は完了
 * 3. 足りなかった場合のみカードを差し押さえ
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  SEIZURE_CONSTANTS,
  autoSeize,
  manualSeize,
  previewSeizure,
  removeSeizedCards,
  clearAllInvestmentPositions,
} from '../systems/seizureManager';
import { addToPawnShop, createInitialPawnShopState } from '../systems/pawnShopManager';
import { createInitialDebtState, repayWithAllGold } from '../systems/debtManager';
import { useIsMobile } from '../../hooks/useMediaQuery';

const SeizureScreen = ({
  playerData,
  updatePlayerData,
  currentBattleNumber,
  getCardValue, // カード基礎価格取得関数
  allCards, // カードリスト（カード名取得用）
  onComplete, // 処理完了時のコールバック
}) => {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState('init'); // 'init' | 'gold_repaid' | 'select' | 'manual' | 'auto' | 'result'
  const [selectedCards, setSelectedCards] = useState([]);
  const [result, setResult] = useState(null);
  const [goldRepaymentResult, setGoldRepaymentResult] = useState(null);

  const collection = playerData.collection || [];
  const originalDebtAmount = playerData.investmentData?.debt?.amount || 0;
  // 所持金返済後の残額（返済処理後はgoldRepaymentResultから取得）
  const debtAmount = goldRepaymentResult
    ? goldRepaymentResult.remainingDebt
    : originalDebtAmount;

  // 初期化時に所持金返済を自動実行
  useEffect(() => {
    if (mode !== 'init') return;

    const currentGold = playerData.gold || 0;
    const debtData = playerData.investmentData?.debt;

    if (!debtData || debtData.amount <= 0) {
      // 借金がない場合は完了
      onComplete && onComplete({ isFullyPaid: true, goldUsed: 0 });
      return;
    }

    // 所持金をすべて返済に使用
    const repayResult = repayWithAllGold(debtData, currentGold);

    // プレイヤーデータを更新
    updatePlayerData((prev) => ({
      ...prev,
      gold: (prev.gold || 0) - repayResult.goldUsed,
      investmentData: {
        ...prev.investmentData,
        debt: repayResult.newDebtData,
      },
    }));

    setGoldRepaymentResult({
      goldUsed: repayResult.goldUsed,
      remainingDebt: repayResult.remainingDebt,
      isFullyPaid: repayResult.isFullyPaid,
      originalDebt: debtData.amount,
    });

    if (repayResult.isFullyPaid) {
      // 所持金で完済できた場合
      setMode('gold_repaid');
    } else {
      // まだ借金が残っている場合はカード差し押さえ選択へ
      setMode('select');
    }
  }, [mode, playerData.gold, playerData.investmentData?.debt, updatePlayerData, onComplete]);

  // カード名取得ヘルパー
  const getCardName = useCallback((cardId) => {
    if (!allCards) return cardId;
    const cardData = allCards.find(c => c.id === cardId);
    return cardData?.name || cardId;
  }, [allCards]);

  // 差し押さえプレビュー
  const preview = useMemo(
    () => previewSeizure(collection, debtAmount, getCardValue),
    [collection, debtAmount, getCardValue]
  );

  // 選択中の合計返済額
  const selectedTotal = useMemo(() => {
    return selectedCards.reduce((total, card) => {
      const value = getCardValue(card);
      return total + Math.floor(value * SEIZURE_CONSTANTS.REPAYMENT_RATE);
    }, 0);
  }, [selectedCards, getCardValue]);

  // カード選択切り替え（インデックスで識別）
  const toggleCardSelection = useCallback((card, index) => {
    setSelectedCards((prev) => {
      const exists = prev.some(c => c._collectionIndex === index);
      if (exists) {
        return prev.filter(c => c._collectionIndex !== index);
      } else {
        return [...prev, { ...card, _collectionIndex: index }];
      }
    });
  }, []);

  // 自動差し押さえ実行
  const handleAutoSeize = () => {
    const seizureResult = autoSeize(collection, debtAmount, getCardValue);

    // FX/ファンドポジションを強制決済
    const investResult = clearAllInvestmentPositions(playerData.investmentData || {});

    // プレイヤーデータ更新
    const newCollection = removeSeizedCards(collection, seizureResult.seizedCards);
    const currentPawnShop = investResult.newInvestmentData?.pawnShop || createInitialPawnShopState();
    const newPawnShop = addToPawnShop(currentPawnShop, seizureResult.pawnItems, currentBattleNumber);

    // 返却証拠金を借金返済に充当
    const marginRepayment = Math.min(investResult.totalMargin, seizureResult.remainingDebt);
    const finalRemainingDebt = Math.max(0, seizureResult.remainingDebt - marginRepayment);
    const surplusMargin = Math.max(0, investResult.totalMargin - marginRepayment);

    updatePlayerData((prev) => ({
      ...prev,
      collection: newCollection,
      gold: (prev.gold || 0) + surplusMargin, // 余剰証拠金は所持金に
      investmentData: {
        ...investResult.newInvestmentData,
        debt: finalRemainingDebt > 0
          ? { ...prev.investmentData.debt, amount: finalRemainingDebt }
          : createInitialDebtState(),
        pawnShop: newPawnShop,
      },
    }));

    setResult({
      ...seizureResult,
      remainingDebt: finalRemainingDebt,
      marginRepayment,
      investmentCleared: {
        fundCount: investResult.fundClosedCount,
        fxCount: investResult.fxClosedCount,
        totalMargin: investResult.totalMargin,
      },
      mode: 'auto',
    });
    setMode('result');
  };

  // 手動差し押さえ実行
  const handleManualSeize = () => {
    if (selectedCards.length === 0) return;

    // _collectionIndex を除去してからmanualSeizeに渡す
    const cleanedCards = selectedCards.map(({ _collectionIndex, ...card }) => card);
    const seizureResult = manualSeize(cleanedCards, debtAmount, getCardValue);

    // FX/ファンドポジションを強制決済
    const investResult = clearAllInvestmentPositions(playerData.investmentData || {});

    // プレイヤーデータ更新
    const newCollection = removeSeizedCards(collection, seizureResult.seizedCards);
    const currentPawnShop = investResult.newInvestmentData?.pawnShop || createInitialPawnShopState();
    const newPawnShop = addToPawnShop(currentPawnShop, seizureResult.pawnItems, currentBattleNumber);

    // 返却証拠金を借金返済に充当
    const marginRepayment = Math.min(investResult.totalMargin, seizureResult.remainingDebt);
    const finalRemainingDebt = Math.max(0, seizureResult.remainingDebt - marginRepayment);
    const surplusMargin = Math.max(0, investResult.totalMargin - marginRepayment);

    updatePlayerData((prev) => ({
      ...prev,
      collection: newCollection,
      gold: (prev.gold || 0) + surplusMargin, // 余剰証拠金は所持金に
      investmentData: {
        ...investResult.newInvestmentData,
        debt: finalRemainingDebt > 0
          ? { ...prev.investmentData.debt, amount: finalRemainingDebt }
          : createInitialDebtState(),
        pawnShop: newPawnShop,
      },
    }));

    setResult({
      ...seizureResult,
      remainingDebt: finalRemainingDebt,
      marginRepayment,
      investmentCleared: {
        fundCount: investResult.fundClosedCount,
        fxCount: investResult.fxClosedCount,
        totalMargin: investResult.totalMargin,
      },
      mode: 'manual',
    });
    setMode('result');
  };

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#1a0000',
      color: '#e0e0e0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      backgroundColor: '#330000',
      borderBottom: '2px solid #ff0000',
      textAlign: 'center',
    },
    warningIcon: {
      fontSize: '48px',
      marginBottom: '8px',
    },
    title: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: 'bold',
      color: '#ff6b6b',
    },
    debtInfo: {
      padding: '16px',
      backgroundColor: 'rgba(255, 0, 0, 0.1)',
      borderBottom: '1px solid #440000',
      textAlign: 'center',
    },
    debtAmount: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: 'bold',
      color: '#ff6b6b',
    },
    debtLabel: {
      fontSize: '14px',
      color: '#888',
      marginTop: '4px',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    modeSelectBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: '400px',
      margin: '0 auto',
    },
    modeButton: {
      padding: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid #444',
      borderRadius: '8px',
      cursor: 'pointer',
      textAlign: 'left',
    },
    modeButtonTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '8px',
      color: '#fff',
    },
    modeButtonDesc: {
      fontSize: '14px',
      color: '#888',
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: '12px',
    },
    cardItem: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cardItemSelected: {
      border: '2px solid #ff6b6b',
      backgroundColor: 'rgba(255, 107, 107, 0.1)',
    },
    cardRarity: {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    cardName: {
      fontSize: '13px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    cardValue: {
      fontSize: '12px',
      color: '#ffd700',
    },
    cardRepayment: {
      fontSize: '11px',
      color: '#ff6b6b',
    },
    progressBar: {
      padding: '16px',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderTop: '1px solid #440000',
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
      fontSize: '14px',
    },
    progressTrack: {
      height: '8px',
      backgroundColor: '#333',
      borderRadius: '4px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#ff6b6b',
      transition: 'width 0.3s',
    },
    actionBar: {
      padding: '16px',
      borderTop: '1px solid #440000',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    confirmButton: {
      width: '100%',
      padding: '14px',
      backgroundColor: '#ff6b6b',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    confirmButtonDisabled: {
      backgroundColor: '#444',
      color: '#888',
      cursor: 'not-allowed',
    },
    resultBox: {
      maxWidth: '500px',
      margin: '0 auto',
      textAlign: 'center',
    },
    resultTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#ff6b6b',
      marginBottom: '24px',
    },
    resultSummary: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    resultRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #333',
    },
    resultCards: {
      maxHeight: '200px',
      overflow: 'auto',
      marginBottom: '16px',
    },
    resultCardItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '4px',
      marginBottom: '4px',
      fontSize: '14px',
    },
  };

  // レアリティの色
  const getRarityColor = (rarity) => {
    const colors = {
      'GR': '#ffd700',
      'UR': '#ff6b6b',
      'SR': '#9d4ce6',
      'R': '#4fc3f7',
      'UC': '#4caf50',
      'C': '#888',
    };
    return colors[rarity] || '#888';
  };

  // 初期化中（所持金返済処理中）
  if (mode === 'init') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.warningIcon}>⏳</div>
          <div style={styles.title}>返済処理中...</div>
        </div>
        <div style={styles.content}>
          <p style={{ textAlign: 'center', color: '#aaa' }}>
            所持金から返済を行っています...
          </p>
        </div>
      </div>
    );
  }

  // 所持金で完済できた場合
  if (mode === 'gold_repaid') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.warningIcon}>✅</div>
          <div style={{ ...styles.title, color: '#4caf50' }}>借金を完済しました</div>
        </div>

        <div style={styles.content}>
          <div style={styles.resultBox}>
            <div style={styles.resultSummary}>
              <div style={styles.resultRow}>
                <span>元の借金額</span>
                <span style={{ color: '#ff6b6b' }}>
                  {goldRepaymentResult?.originalDebt?.toLocaleString() || 0}G
                </span>
              </div>
              <div style={styles.resultRow}>
                <span>所持金から返済</span>
                <span style={{ color: '#4caf50' }}>
                  {goldRepaymentResult?.goldUsed?.toLocaleString() || 0}G
                </span>
              </div>
              <div style={{ ...styles.resultRow, borderBottom: 'none', fontWeight: 'bold' }}>
                <span>残り借金</span>
                <span style={{ color: '#4caf50' }}>0G</span>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#888', marginBottom: '16px' }}>
              所持金だけで借金を返済できました。<br />
              カードの差し押さえはありません。
            </p>

            <button
              style={{ ...styles.confirmButton, backgroundColor: '#4caf50' }}
              onClick={() => onComplete && onComplete({
                isFullyPaid: true,
                goldUsed: goldRepaymentResult?.goldUsed || 0,
                seizedCards: [],
              })}
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 選択モード表示（所持金返済後、まだ借金が残っている場合）
  if (mode === 'select') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.warningIcon}>⚠️</div>
          <div style={styles.title}>返済期限が過ぎました</div>
        </div>

        <div style={styles.debtInfo}>
          <div style={styles.debtAmount}>{debtAmount.toLocaleString()}G</div>
          <div style={styles.debtLabel}>残り借金額</div>
        </div>

        {/* 所持金返済の結果を表示 */}
        {goldRepaymentResult && goldRepaymentResult.goldUsed > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            borderBottom: '1px solid #2e7d32',
          }}>
            <div style={{ fontSize: '14px', color: '#4caf50' }}>
              💰 所持金から {goldRepaymentResult.goldUsed.toLocaleString()}G を返済しました
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              元の借金: {goldRepaymentResult.originalDebt.toLocaleString()}G →
              残り: {goldRepaymentResult.remainingDebt.toLocaleString()}G
            </div>
          </div>
        )}

        <div style={styles.content}>
          <p style={{ textAlign: 'center', marginBottom: '24px', color: '#aaa' }}>
            {goldRepaymentResult && goldRepaymentResult.goldUsed > 0
              ? '所持金だけでは足りませんでした。\nカードを売却して残りを返済してください。'
              : 'カードを売却して返済してください。\n必要額に達するまで選択してください。'}
          </p>

          <div style={styles.modeSelectBox}>
            <div
              style={styles.modeButton}
              onClick={() => setMode('manual')}
            >
              <div style={styles.modeButtonTitle}>🎯 自分で選ぶ</div>
              <div style={styles.modeButtonDesc}>
                売却するカードを手動で選択します。<br />
                思い入れのあるカードを守れます。
              </div>
            </div>

            <div
              style={styles.modeButton}
              onClick={handleAutoSeize}
            >
              <div style={styles.modeButtonTitle}>🔄 おまかせで売却</div>
              <div style={styles.modeButtonDesc}>
                高レアリティ・高価格順に自動で売却します。<br />
                {preview.requiredCards.length}枚のカードが売却されます。
              </div>
            </div>
          </div>

          {!preview.isEnough && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid #ff9800',
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#ff9800', fontWeight: 'bold', marginBottom: '8px' }}>
                ⚠️ カードが不足しています
              </div>
              <div style={{ fontSize: '14px', color: '#aaa' }}>
                全カードを売却しても{preview.shortfall.toLocaleString()}G不足します。<br />
                自動対戦ループで返済します。
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 手動選択モード
  if (mode === 'manual') {
    const progress = Math.min(100, (selectedTotal / debtAmount) * 100);
    const isEnough = selectedTotal >= debtAmount;

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>売却するカードを選択</div>
        </div>

        <div style={styles.debtInfo}>
          <div style={{ fontSize: '14px', color: '#888' }}>残り借金</div>
          <div style={{
            ...styles.debtAmount,
            fontSize: '24px',
          }}>
            {Math.max(0, debtAmount - selectedTotal).toLocaleString()}G
          </div>
        </div>

        <div style={styles.content}>
          <div style={styles.cardGrid}>
            {collection.map((card, index) => {
              const key = `${card.cardId}-${card.rarity}`;
              const isSelected = selectedCards.some(c => c._collectionIndex === index);
              const cardValue = getCardValue(card);
              const repaymentValue = Math.floor(cardValue * SEIZURE_CONSTANTS.REPAYMENT_RATE);

              return (
                <div
                  key={`${key}-${index}`}
                  style={{
                    ...styles.cardItem,
                    ...(isSelected ? styles.cardItemSelected : {}),
                  }}
                  onClick={() => toggleCardSelection(card, index)}
                >
                  <span
                    style={{
                      ...styles.cardRarity,
                      backgroundColor: getRarityColor(card.rarity),
                      color: card.rarity === 'C' || card.rarity === 'UC' ? '#fff' : '#000',
                    }}
                  >
                    {card.rarity}
                  </span>
                  <div style={styles.cardName}>{getCardName(card.cardId)}</div>
                  <div style={styles.cardValue}>{cardValue.toLocaleString()}G</div>
                  <div style={styles.cardRepayment}>→ {repaymentValue.toLocaleString()}G返済</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={styles.progressBar}>
          <div style={styles.progressLabel}>
            <span>返済進捗</span>
            <span>{selectedTotal.toLocaleString()}G / {debtAmount.toLocaleString()}G</span>
          </div>
          <div style={styles.progressTrack}>
            <div
              style={{
                ...styles.progressFill,
                width: `${progress}%`,
                backgroundColor: isEnough ? '#4caf50' : '#ff6b6b',
              }}
            />
          </div>
        </div>

        <div style={styles.actionBar}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              style={{
                ...styles.confirmButton,
                flex: 1,
                backgroundColor: '#444',
              }}
              onClick={() => {
                setMode('select');
                setSelectedCards([]);
              }}
            >
              戻る
            </button>
            <button
              style={{
                ...styles.confirmButton,
                flex: 2,
                ...(selectedCards.length === 0 ? styles.confirmButtonDisabled : {}),
              }}
              onClick={handleManualSeize}
              disabled={selectedCards.length === 0}
            >
              {isEnough ? '確定' : `${selectedCards.length}枚売却（不足分は自動対戦）`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 結果表示
  if (mode === 'result') {
    // 合計返済額（所持金 + カード売却）
    const totalGoldRepayment = goldRepaymentResult?.goldUsed || 0;
    const totalCardRepayment = result.totalRepayment;
    const grandTotalRepayment = totalGoldRepayment + totalCardRepayment;
    const originalTotalDebt = goldRepaymentResult?.originalDebt || (result.remainingDebt + totalCardRepayment);

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.warningIcon}>📋</div>
          <div style={styles.title}>差し押さえ完了</div>
        </div>

        <div style={styles.content}>
          <div style={styles.resultBox}>
            <div style={styles.resultSummary}>
              {/* 元の借金額 */}
              <div style={styles.resultRow}>
                <span>元の借金額</span>
                <span style={{ color: '#ff6b6b' }}>{originalTotalDebt.toLocaleString()}G</span>
              </div>
              {/* 所持金返済 */}
              {totalGoldRepayment > 0 && (
                <div style={styles.resultRow}>
                  <span>所持金から返済</span>
                  <span style={{ color: '#4caf50' }}>{totalGoldRepayment.toLocaleString()}G</span>
                </div>
              )}
              {/* カード売却 */}
              <div style={styles.resultRow}>
                <span>カード売却 ({result.seizedCards.length}枚)</span>
                <span style={{ color: '#4caf50' }}>{totalCardRepayment.toLocaleString()}G</span>
              </div>
              {/* 合計返済額 */}
              {totalGoldRepayment > 0 && (
                <div style={{ ...styles.resultRow, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                  <span style={{ fontWeight: 'bold' }}>合計返済額</span>
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}>{grandTotalRepayment.toLocaleString()}G</span>
                </div>
              )}
              {/* 残り借金 */}
              <div style={styles.resultRow}>
                <span>残り借金</span>
                <span style={{ color: result.remainingDebt > 0 ? '#ff6b6b' : '#4caf50' }}>
                  {result.remainingDebt.toLocaleString()}G
                </span>
              </div>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>売却されたカード:</div>
              <div style={styles.resultCards}>
                {result.seizedCards.map((card, index) => (
                  <div key={index} style={styles.resultCardItem}>
                    <span>
                      <span style={{ color: getRarityColor(card.rarity) }}>[{card.rarity}]</span>
                      {' '}{getCardName(card.cardId)}
                    </span>
                    <span style={{ color: '#ff6b6b' }}>
                      {card.repaymentValue.toLocaleString()}G
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#888', marginBottom: '16px' }}>
              売却されたカードは質屋で買い戻すことができます。
            </p>

            {result.remainingDebt > 0 && (
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid #ff9800',
                borderRadius: '8px',
                marginBottom: '16px',
              }}>
                <div style={{ color: '#ff9800', fontWeight: 'bold' }}>
                  まだ{result.remainingDebt.toLocaleString()}Gの借金が残っています
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                  自動対戦で返済を続けます...
                </div>
              </div>
            )}

            <button
              style={styles.confirmButton}
              onClick={() => onComplete && onComplete(result)}
            >
              {result.remainingDebt > 0 ? '自動対戦へ' : '閉じる'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SeizureScreen;
